import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { apiClient } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { CallbackForm } from '@/components/partners/CallbackForm';
import { ShareButtons } from '@/components/partners/ShareButtons';
import { formatINR, priceRange, firstImage } from '@/components/partners/utils';
import { propertyPath, extractAgentId } from '@/utils/slug';
import {
  Star, MapPin, CheckCircle, ArrowLeft, Briefcase, Award,
  Building2, Home, BedDouble, Maximize, ShieldCheck,
} from 'lucide-react';

const StatPill = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-xl border border-white/15">
    <Icon className="w-4 h-4 text-emerald-300" />
    <div className="leading-tight">
      <p className="text-sm font-semibold text-white">{value}</p>
      <p className="text-[11px] text-emerald-200/80">{label}</p>
    </div>
  </div>
);

const ListingCard = ({ listing }) => {
  const price = formatINR(listing.price) || 'Price on request';
  return (
    <Link
      to={propertyPath({ ...listing, property_id: listing.listing_id })}
      className="group block bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
      data-testid={`agent-listing-${listing.listing_id}`}
    >
      <div className="relative h-44 overflow-hidden bg-gray-100">
        <img
          src={firstImage(listing.images)}
          alt={listing.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {listing.is_boosted && (
          <span className="absolute top-3 left-3 text-[11px] font-semibold bg-amber-500 text-white px-2.5 py-1 rounded-full shadow">
            Featured
          </span>
        )}
        <span className="absolute bottom-3 left-3 text-sm font-bold bg-black/70 text-white px-3 py-1 rounded-lg backdrop-blur">
          {price}
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 truncate">{listing.title}</h3>
        <p className="flex items-center gap-1 text-sm text-gray-500 mt-1">
          <MapPin className="w-3.5 h-3.5" /> {[listing.locality, listing.city].filter(Boolean).join(', ') || '—'}
        </p>
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
          {listing.bedrooms ? <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {listing.bedrooms} BHK</span> : null}
          {listing.area ? <span className="flex items-center gap-1"><Maximize className="w-3.5 h-3.5" /> {listing.area} {listing.area_unit || 'sqft'}</span> : null}
          {listing.property_type ? <span className="flex items-center gap-1"><Home className="w-3.5 h-3.5" /> {listing.property_type}</span> : null}
        </div>
      </div>
    </Link>
  );
};

export default function AgentProfile() {
  const { slugId } = useParams();
  const agentId = extractAgentId(slugId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get(`/public/agent/${agentId}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Agent not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [agentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4"><div className="h-96 bg-gray-200 rounded-2xl animate-pulse" /></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-gray-500 mb-4" data-testid="agent-not-found">{error || 'Agent not found'}</p>
          <Link to="/buy/properties"><Button variant="outline">Browse properties</Button></Link>
        </div>
      </div>
    );
  }

  const a = data.agent;
  const stats = a.stats || {};
  const title = `${a.name}${a.company ? ` · ${a.company}` : ''} — Real Estate Agent`;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="agent-profile-page">
      <Helmet>
        <title>{title} | ZenStudios</title>
        <meta name="description" content={(a.bio || `${a.name} is a verified real estate agent in ${a.city}. View ${data.listings_count} active listings and request a callback.`).slice(0, 160)} />
        <meta property="og:title" content={title} />
        <meta property="og:type" content="profile" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-950 via-teal-900 to-emerald-900 text-white">
        <div className="max-w-5xl mx-auto px-4 pt-24 pb-12">
          <div className="flex items-center justify-between gap-3 mb-8">
            <Link to="/buy/properties" className="inline-flex items-center gap-1 text-emerald-300 hover:text-white text-sm transition-colors" data-testid="agent-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to properties
            </Link>
            <ShareButtons title={a.name} testidPrefix="agent-share" />
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-24 h-24 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center text-4xl font-bold overflow-hidden flex-shrink-0">
              {a.profile_image ? <img src={a.profile_image} alt={a.name} className="w-full h-full object-cover" /> : a.name?.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold" data-testid="agent-name">{a.name}</h1>
                {a.verified && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-200 bg-emerald-500/20 px-3 py-1 rounded-full" data-testid="agent-verified-badge">
                    <ShieldCheck className="w-3.5 h-3.5" /> Verified Agent
                  </span>
                )}
              </div>
              {a.company && <p className="text-emerald-200 mt-1 flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {a.company}</p>}
              <p className="text-emerald-200/80 mt-1 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {[a.city, a.state].filter(Boolean).join(', ')}</p>
              <div className="flex flex-wrap gap-3 mt-5">
                <StatPill icon={Home} label="Active listings" value={data.listings_count} />
                {a.experience ? <StatPill icon={Award} label="Years experience" value={`${a.experience}+`} /> : null}
                <StatPill icon={Star} label="Total leads served" value={stats.total_leads || 0} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-8 grid lg:grid-cols-3 gap-8">
        {/* Main */}
        <div className="lg:col-span-2 space-y-8">
          {a.bio && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="agent-about">
              <h2 className="font-semibold text-gray-900 mb-3">About {a.name.split(' ')[0]}</h2>
              <p className="text-gray-600 leading-relaxed">{a.bio}</p>
              {a.rera_number && (
                <p className="text-xs text-gray-400 mt-4 flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> RERA: {a.rera_number}
                </p>
              )}
            </div>
          )}

          <div data-testid="agent-listings-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900 text-lg">Active Listings</h2>
              <span className="text-sm text-gray-400">{data.listings_count} properties</span>
            </div>
            {data.listings.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center text-gray-400" data-testid="agent-no-listings">
                <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No active listings right now. Request a callback to discuss your requirement.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                {data.listings.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
              </div>
            )}
          </div>
        </div>

        {/* Sticky contact */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-100 p-6 lg:sticky lg:top-24" data-testid="agent-contact-card">
            <h3 className="font-semibold text-gray-900 mb-1">Interested? Get in touch</h3>
            <CallbackForm
              endpoint={`/public/agent/${agentId}/callback`}
              accent="emerald"
              testidPrefix="agent-callback"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
